import { useState } from "react";

import { Button, TextField } from "../../components/ui/Controls";
import { AlertBanner, EmptyState, Skeleton, StatusBadge } from "../../components/ui/Feedback";
import { apiErrorMessage } from "../../lib/auth";
import { operationsApi, type OperationsLogPage } from "../../lib/api";
import { observationStateLabel } from "../../lib/operation-events";

export function RuntimeLogViewer({ projectId }: { readonly projectId: string }) {
  const [jobId, setJobId] = useState("");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState<OperationsLogPage>();
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);

  const load = async (cursor?: string) => {
    if (jobId.trim() === "") return;
    setBusy(true);
    setError(undefined);
    try {
      const next = await operationsApi.runtimeLogs({
        projectId,
        jobId: jobId.trim(),
        ...(cursor === undefined ? {} : { cursor }),
        ...(query.trim() === "" ? {} : { query: query.trim() }),
      });
      setPage((current) =>
        cursor === undefined
          ? next
          : {
              ...next,
              items: [...(current?.items ?? []), ...next.items],
            },
      );
    } catch (caught) {
      setError(apiErrorMessage(caught));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section aria-labelledby="runtime-log-heading" className="card stack">
      <div>
        <p className="eyebrow">Sınırlandırılmış kanıt</p>
        <h2 id="runtime-log-heading">Çalışma günlükleri</h2>
        <p className="muted">
          Günlükler sunucudan doğrudan okunmaz. Yalnızca yetkili, özet ve uzunluğu doğrulanmış
          parçalar gösterilir.
        </p>
      </div>
      <div className="form-stack">
        <TextField
          autoComplete="off"
          label="İş kimliği"
          onChange={(event) => setJobId(event.currentTarget.value)}
          placeholder="İş kimliğini yapıştırın"
          value={jobId}
        />
        <TextField
          autoComplete="off"
          label="Günlükte ara (isteğe bağlı)"
          maxLength={256}
          onChange={(event) => setQuery(event.currentTarget.value)}
          value={query}
        />
        <div className="form-actions">
          <Button
            busy={busy}
            busyLabel="Günlükler yükleniyor…"
            disabled={jobId.trim() === ""}
            onClick={() => void load()}
          >
            Günlükleri göster
          </Button>
          {jobId.trim() === "" ? null : (
            <a
              className="button button--secondary"
              href={operationsApi.runtimeLogDownloadPath(projectId, jobId.trim())}
            >
              Yetkili indirmeyi aç
            </a>
          )}
        </div>
      </div>
      {busy && page === undefined ? <Skeleton label="Çalışma günlükleri yükleniyor" /> : null}
      {error === undefined ? null : (
        <AlertBanner heading="Günlükler alınamadı" tone="error">
          <p>{error}</p>
        </AlertBanner>
      )}
      {page === undefined ? null : (
        <>
          <StatusBadge
            label={observationStateLabel(page.state)}
            status={
              page.state === "confirmed"
                ? "healthy"
                : page.state === "stale"
                  ? "warning"
                  : "offline"
            }
          />
          {page.items.length === 0 ? (
            <EmptyState
              body="Bu aralıkta doğrulanmış günlük parçası yok."
              heading="Günlük bulunamadı"
            />
          ) : (
            <div aria-label="Çalışma günlükleri" className="log-viewer stack stack--tight">
              {page.items.map((item) => (
                <article key={item.cursor}>
                  <header className="split-row">
                    <span>{streamLabel(item.stream)}</span>
                    <time dateTime={item.observedAt}>
                      {new Date(item.observedAt).toLocaleString("tr-TR")}
                    </time>
                  </header>
                  <pre>{item.text}</pre>
                </article>
              ))}
            </div>
          )}
          {page.nextCursor === undefined ? null : (
            <Button busy={busy} onClick={() => void load(page.nextCursor)} variant="secondary">
              Daha fazla günlük yükle
            </Button>
          )}
        </>
      )}
    </section>
  );
}

function streamLabel(stream: "stdout" | "stderr" | "system"): string {
  if (stream === "stdout") return "Uygulama çıktısı";
  if (stream === "stderr") return "Hata çıktısı";
  return "Sistem";
}
